/**
 * The psalter index — all 150, one route.
 *
 * Shows the Latin incipit beside each number because that is how psalms are cited
 * in choral repertoire ("Cantate Domino", "Jubilate Deo") at least as often as by
 * number, so the index doubles as the lookup from a title on a piece of music to
 * the psalm it sets.
 */
import { Link } from 'react-router-dom'
import { Breadcrumbs } from '@gjl/studio-chrome'

import { psalter, BCP_1979_PSALTER } from '@/data/psalter'
import { PageHead } from '@/components/PageHead'

const base = '/hymnal/psalter'

export default function PsalterIndexPage() {
  const verses = psalter.psalms.reduce((a, p) => a + p.verseCount, 0)

  return (
    <div className="mx-auto max-w-3xl px-5 py-10">
      <PageHead
        title="The Psalter — Book of Common Prayer 1979"
        description={`All 150 psalms of the 1979 Book of Common Prayer Psalter in full — ${verses} verses, free to read and download.`}
        path={base}
        suffix="The Psalter (BCP 1979)"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Book',
          name: BCP_1979_PSALTER.translation,
          about: 'The Psalms',
          inLanguage: 'en',
          isAccessibleForFree: true,
          license: 'https://en.wikipedia.org/wiki/Public_domain',
        }}
      />

      <Breadcrumbs className="mb-6" items={[{ name: 'The Psalter', url: base }]} />

      <header className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">The Psalter</h1>
        <p className="mt-2 text-muted-foreground">
          All 150 psalms as the {BCP_1979_PSALTER.translation} prints them — {verses.toLocaleString()} verses, each
          divided at the asterisk.
        </p>
        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{BCP_1979_PSALTER.licence}</p>
        {/* Sibling-corpus link. Both books live under /hymnal/* and a reader who
            wants one plausibly wants the other; without it the two corpora sit
            siloed and neither passes link equity to the other. Deliberately NOT a
            per-hymn/per-psalm mapping — the dataset carries no scripture
            references, so a claim that hymn 645 sets Psalm 23 would be invented
            rather than sourced. See the spec's open question. */}
        <p className="mt-4 text-sm">
          <Link
            to="/hymnal/hymnal1982"
            className="underline decoration-border underline-offset-4 hover:decoration-foreground"
          >
            The Hymnal 1982
          </Link>
          <span className="text-muted-foreground"> — 720 hymns, with tunes, metres and credits.</span>
        </p>
      </header>

      <ul className="grid gap-x-8 sm:grid-cols-2">
        {psalter.psalms.map((p) => (
          <li key={p.slug} className="border-b border-border py-2 last:border-0">
            <Link to={`${base}/${p.slug}`} className="group flex gap-3">
              <span className="w-9 shrink-0 pt-0.5 text-right font-mono text-xs text-muted-foreground">{p.number}</span>
              <span className="min-w-0">
                <span className="block text-sm text-foreground group-hover:underline">{p.title}</span>
                {/* Absent for 8 psalms; see PsalmPage for why we never invent one. */}
                {p.latinTitle && (
                  <span className="block truncate text-xs italic text-muted-foreground">{p.latinTitle}</span>
                )}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
